import { Worker } from './remote-worker';

const availableWorkers = {
  'liquid-renderer': {
    worker: null,
    module: null,
    sessions: new Map(),
    config: {
      createInstance: () =>
        new Worker(
          /* webpackPreload: true */
          /* webpackChunkName: "liquid-renderer.worker" */
          new URL('./liquid-renderer.worker.js', import.meta.url)
        ),
      loadModule: async () => {
        const module = await import(
          /* webpackPreload: true */
          /* webpackChunkName: "liquid-renderer.module.worker" */
          './liquid-renderer.worker.js'
        );
        availableWorkers['liquid-renderer'].module = module;
      },
    },
  },
  analytics: {
    worker: null,
    sessions: new Map(),
    config: {
      createInstance: () =>
        new Worker(
          /* webpackPrefetch: true */
          /* webpackChunkName: "analytics.worker" */
          new URL('./analytics.worker.js', import.meta.url)
        ),

      loadModule: async () => {
        const module = await import(
          /* webpackPrefetch: true */
          /* webpackChunkName: "analytics.module.worker" */
          './analytics.worker.js'
        );

        availableWorkers['analytics'].module = module;
      },
    },
  },
};

function createSessionInfo() {
  return {
    id: Math.random().toString(36).substring(2),
    resolve: null,
    reject: null,
    timeout: null,
  };
}

function isSafari() {
  return navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
}

function shouldUseModule() {
  return (
    import.meta?.env?.DEV ||
    isSafari() ||
    window.boostWidgetIntegrationConfig?.workers?.useModule === true
  );
}

function setupWorker(workerName) {
  const worker = availableWorkers[workerName].config.createInstance();
  availableWorkers[workerName].worker = worker;

  worker.onmessage = function (event) {
    const {
      data: { sessionId, response },
    } = event;

    const sessions = availableWorkers[workerName].sessions;

    const sessionInfo = sessions.get(sessionId);

    if (sessionInfo) {
      sessionInfo.resolve(response);

      clearTimeout(sessionInfo.timeout);
      sessions.delete(sessionId);
    }
  };

  if (shouldUseModule()) {
    availableWorkers[workerName].config.loadModule?.();
  }
}

export function warmupWorkers() {
  for (const workerName in availableWorkers) {
    if (Object.prototype.hasOwnProperty.call(availableWorkers, workerName)) {
      if (!availableWorkers[workerName].worker) {
        setupWorker(workerName);
      }
    }
  }
}

export function startSession(workerName) {
  const workerModule = availableWorkers[workerName];

  const { worker, sessions } = workerModule;

  if (!worker) {
    setupWorker(workerName);
  }

  const sessionInfo = createSessionInfo();

  return {
    async send(payload) {
      const event = {
        sessionId: sessionInfo.id,
        payload,
      };

      if (shouldUseModule() && workerModule.config.loadModule) {
        if (!workerModule.module) {
          await workerModule.config.loadModule();
        }
        const { module } = workerModule;
        const result = await module.default({
          data: event,
        });

        return result?.response;
      }

      return new Promise((resolve, reject) => {
        sessionInfo.resolve = resolve;
        sessionInfo.reject = reject;

        sessions.set(sessionInfo.id, sessionInfo);

        worker.postMessage(event);

        const timeout = setTimeout(() => {
          const session = sessions.get(sessionInfo.id);
          if (session) {
            session.reject(new Error('Worker timeout'));
            sessions.delete(sessionInfo.id);
          }
        }, 10000);

        sessionInfo.timeout = timeout;
      });
    },
  };
}
